import { z } from 'zod';

import { isImgixUrl } from '@air/component-imgix-image';

export type ImageMetadata = {
  contentType: string;
  height: number;
  width: number;
};

const FALLBACK_CONTENT_TYPE = 'image/png';

const EXTENSION_TO_MIME: Record<string, string> = {
  avif: 'image/avif',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  webp: 'image/webp',
};

const getContentTypeFromExtension = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname;

    const extension = pathname.split('.').pop()?.toLowerCase();

    if (extension && extension in EXTENSION_TO_MIME) {
      return EXTENSION_TO_MIME[extension];
    }
  } catch (error) {
    console.warn('[getImageMetadata] URL parse error:', error);
  }
  return FALLBACK_CONTENT_TYPE;
};

const imgixJsonResponseSchema = z.object({
  'Content-Type': z.string().optional(),
  PixelHeight: z.number(),
  PixelWidth: z.number(),
});

const getImageMetadataFromImgix = async (url: string): Promise<ImageMetadata> => {
  const jsonUrl = new URL(url);
  jsonUrl.searchParams.set('fm', 'json');

  const response = await fetch(jsonUrl.toString());

  if (!response.ok) {
    throw new Error(`Imgix request failed: ${response.status}`);
  }

  const rawData: unknown = await response.json();
  const parseResult = imgixJsonResponseSchema.safeParse(rawData);

  if (!parseResult.success) {
    throw new Error(`Invalid Imgix response: ${parseResult.error.message}`);
  }

  const data = parseResult.data;

  return {
    contentType: data['Content-Type'] ?? getContentTypeFromExtension(url),
    height: data.PixelHeight,
    width: data.PixelWidth,
  };
};

const getImageMetadataFromGenericUrl = async (url: string): Promise<ImageMetadata> => {
  const fallbackContentType = getContentTypeFromExtension(url);

  const contentTypePromise = fetch(url, { method: 'HEAD' })
    .then((response) => response.headers.get('Content-Type') ?? fallbackContentType)
    .catch(() => fallbackContentType);

  const dimensionsPromise = new Promise<{ height: number; width: number }>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      resolve({
        height: img.naturalHeight,
        width: img.naturalWidth,
      });
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image from URL: ${url}`));
    };

    img.src = url;
  });

  const [contentType, dimensions] = await Promise.all([contentTypePromise, dimensionsPromise]);

  return {
    contentType,
    height: dimensions.height,
    width: dimensions.width,
  };
};

/**
 * Fetches image metadata (dimensions and content type) from a URL.
 * Uses imgix JSON API for *.imgix.net URLs, falls back to image loading for others.
 *
 * @param url - The image URL to fetch metadata for
 * @returns Promise with width, height, and contentType
 */
export const getImageMetadata = async (url: string): Promise<ImageMetadata> => {
  if (isImgixUrl(url)) {
    try {
      return await getImageMetadataFromImgix(url);
    } catch (error) {
      console.warn('[getImageMetadata] Imgix fetch failed, using fallback:', error);
    }
  }

  return getImageMetadataFromGenericUrl(url);
};
